import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Select,
  SelectChangeEvent,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  LinearProgress,
  Divider,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Vaccines as VaccinesIcon,
  AutoFixHigh as AutoFixIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Star as CriticalIcon,
} from '@mui/icons-material';
import {
  usePreventiveProgrammes,
  usePreventiveProgramme,
  useUpdateProgrammeEvent,
  useStoreProgramme,
  useStoreProgrammeEvent,
  useRegenerateProgramme,
  usePHProducts,
} from '../../hooks/usePreventiveHealth';
import { useQueryClient } from '@tanstack/react-query';

// ── Constants ──────────────────────────────────────────────────────────────────

const TRACK_COLORS: Record<string, string> = {
  vaccination: '#42a5f5',
  antiparasitic: '#ff9800',
  vitamin: '#66bb6a',
  biosecurity: '#ab47bc',
  monitoring: '#ef5350',
};

const TRACK_LABELS: Record<string, string> = {
  vaccination: 'Vaccination',
  antiparasitic: 'Antiparasitic',
  vitamin: 'Vitamin / Electrolyte',
  biosecurity: 'Biosecurity',
  monitoring: 'Monitoring & Inspection',
};

const TRACKS = Object.keys(TRACK_LABELS);

const ROUTE_OPTIONS = [
  { value: 'drinking_water', label: 'Drinking Water' },
  { value: 'injection', label: 'Injection' },
  { value: 'eye_drop', label: 'Eye Drop' },
  { value: 'spray', label: 'Spray' },
  { value: 'wing_web', label: 'Wing Web' },
  { value: 'feed', label: 'Feed' },
  { value: 'topical', label: 'Topical' },
  { value: 'environmental', label: 'Environmental' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatTrigger(event: any): string {
  const tv = event.trigger_value ?? {};
  switch (event.trigger_type) {
    case 'age_weeks':
      return `Week ${tv.week ?? tv.weeks ?? '?'}`;
    case 'age_days':
      return `Day ${tv.day ?? tv.days ?? '?'}`;
    case 'calendar_recurring': {
      const freq = event.recurrence?.frequency ?? 'recurring';
      return `${freq} (recurring)`;
    }
    case 'dependency': {
      const dep = event.depends_on;
      if (!dep) return 'Dependency';
      const offset = dep.offset_days ?? 0;
      return `After ${dep.name.split(' ').slice(0, 3).join(' ')} +${offset}d`;
    }
    case 'batch_milestone':
      return `Milestone: ${tv.milestone ?? ''}`;
    default:
      return event.trigger_type ?? '—';
  }
}

// ── EventEditDialog ────────────────────────────────────────────────────────────

interface EventEditDialogProps {
  event: any;
  allEvents: any[];
  products: any[];
  onClose: () => void;
  onSave: (data: any) => void;
  saving: boolean;
}

const EventEditDialog: React.FC<EventEditDialogProps> = ({
  event,
  allEvents,
  products,
  onClose,
  onSave,
  saving,
}) => {
  const [form, setForm] = useState({
    name: event.name ?? '',
    description: event.description ?? '',
    product_id: event.product?.id ?? '',
    dose_instructions: event.dose_instructions ?? '',
    route: event.route ?? '',
    egg_withdrawal_days: event.egg_withdrawal_days ?? '',
    is_active: event.is_active ?? true,
    is_critical: event.is_critical ?? false,
    depends_on_event_id: event.depends_on?.id ?? '',
    offset_days: event.depends_on?.offset_days ?? 0,
  });

  const set =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = () => {
    const payload: any = {
      name: form.name,
      description: form.description || null,
      product_id: form.product_id || null,
      dose_instructions: form.dose_instructions || null,
      route: form.route || null,
      egg_withdrawal_days:
        form.egg_withdrawal_days !== '' ? Number(form.egg_withdrawal_days) : null,
      is_active: form.is_active,
      is_critical: form.is_critical,
      depends_on_event_id: form.depends_on_event_id || null,
      offset_days: form.depends_on_event_id ? Number(form.offset_days) : null,
    };
    onSave(payload);
  };

  const otherEvents = allEvents.filter((e) => e.id !== event.id);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {event.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {event.event_code} · {TRACK_LABELS[event.track] ?? event.track}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Name" value={form.name} onChange={set('name')} fullWidth size="small" />
          <TextField
            label="Description"
            value={form.description}
            onChange={set('description')}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Product</InputLabel>
            <Select value={form.product_id} label="Product" onChange={set('product_id')}>
              <MenuItem value="">None</MenuItem>
              {products.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Dose instructions"
            value={form.dose_instructions}
            onChange={set('dose_instructions')}
            fullWidth
            size="small"
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Route</InputLabel>
            <Select value={form.route} label="Route" onChange={set('route')}>
              <MenuItem value="">None</MenuItem>
              {ROUTE_OPTIONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Egg withdrawal days"
            type="number"
            value={form.egg_withdrawal_days}
            onChange={set('egg_withdrawal_days')}
            fullWidth
            size="small"
            inputProps={{ min: 0 }}
          />

          <Divider />

          <FormControl size="small" fullWidth>
            <InputLabel>Depends on event</InputLabel>
            <Select
              value={form.depends_on_event_id}
              label="Depends on event"
              onChange={set('depends_on_event_id')}
            >
              <MenuItem value="">None</MenuItem>
              {otherEvents.map((e: any) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.depends_on_event_id && (
            <TextField
              label="Offset days after dependency"
              type="number"
              value={form.offset_days}
              onChange={set('offset_days')}
              fullWidth
              size="small"
            />
          )}

          <Divider />

          <Box display="flex" gap={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  color="success"
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_critical}
                  onChange={(e) => setForm((f) => ({ ...f, is_critical: e.target.checked }))}
                  color="warning"
                />
              }
              label="Critical"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.name}
          sx={{ color: '#fff' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── SaveVersionDialog ──────────────────────────────────────────────────────────

interface SaveVersionDialogProps {
  programme: any;
  onClose: () => void;
  onConfirm: (name: string, version: string) => void;
  copying: boolean;
  copyProgress: number;
  copyDone: boolean;
}

const SaveVersionDialog: React.FC<SaveVersionDialogProps> = ({
  programme,
  onClose,
  onConfirm,
  copying,
  copyProgress,
  copyDone,
}) => {
  const nextVersion = (() => {
    const current = programme?.version ?? '1.0';
    const parts = String(current).split('.');
    const minor = parseInt(parts[1] ?? '0', 10) + 1;
    return `${parts[0]}.${minor}`;
  })();

  const [name, setName] = useState(`${programme?.name ?? ''} v${nextVersion}`);
  const [version, setVersion] = useState(nextVersion);

  return (
    <Dialog open onClose={!copying ? onClose : undefined} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={700}>Save as New Version</DialogTitle>
      <DialogContent dividers>
        {copyDone ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={2}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
            <Typography variant="body1" fontWeight={600} textAlign="center">
              New version created successfully
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Note: event dependencies were not copied and will need to be re-configured.
            </Typography>
          </Box>
        ) : copying ? (
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Typography variant="body2" color="text.secondary">
              Copying programme events… {copyProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={copyProgress} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              Creates a new programme with the same events. Existing batches using the current
              version are unaffected. Activate the new version when ready and assign it to new
              batches.
            </Alert>
            <TextField
              label="New programme name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Version tag"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              fullWidth
              size="small"
            />
            <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
              Event dependencies will not be copied and must be re-set manually.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {copyDone ? (
          <Button variant="contained" onClick={onClose} sx={{ color: '#fff' }}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={copying}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => onConfirm(name, version)}
              disabled={copying || !name}
              startIcon={<CopyIcon />}
              sx={{ color: '#fff' }}
            >
              Create Copy
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ── TrackSection ───────────────────────────────────────────────────────────────

interface TrackSectionProps {
  track: string;
  events: any[];
  onEdit: (event: any) => void;
  onToggleActive: (event: any, active: boolean) => void;
  toggling: number | null;
}

const TrackSection: React.FC<TrackSectionProps> = ({
  track,
  events,
  onEdit,
  onToggleActive,
  toggling,
}) => {
  const color = TRACK_COLORS[track] ?? '#9e9e9e';

  return (
    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
      {/* Track header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: `1px solid ${color}33`,
          background: `linear-gradient(90deg, ${color}18 0%, transparent 100%)`,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color, letterSpacing: '0.5px' }}>
          {TRACK_LABELS[track]?.toUpperCase()}
        </Typography>
        <Chip
          label={`${events.length} event${events.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{ ml: 0.5, fontSize: '0.7rem', bgcolor: `${color}22`, color }}
        />
        <Chip
          label={`${events.filter((e) => e.is_active).length} active`}
          size="small"
          sx={{ fontSize: '0.7rem', bgcolor: 'transparent', color: 'text.secondary' }}
        />
      </Box>

      {events.length === 0 ? (
        <Box px={3} py={2}>
          <Typography variant="body2" color="text.disabled">
            No events in this track.
          </Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.4px',
                  py: 1,
                },
              }}
            >
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Dose</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell align="center">Withdraw</TableCell>
              <TableCell align="center">Critical</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow
                key={event.id}
                sx={{
                  opacity: event.is_active ? 1 : 0.45,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <TableCell sx={{ color: 'text.disabled', width: 40 }}>
                  {event.sequence_order ?? '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {event.name}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {event.event_code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{event.product?.name ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 160,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {event.dose_instructions ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatTrigger(event)}
                    size="small"
                    sx={{ fontSize: '0.7rem', bgcolor: `${color}18`, color, fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" color="warning.main" fontWeight={600}>
                    {event.egg_withdrawal_days != null ? `${event.egg_withdrawal_days}d` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {event.is_critical && (
                    <Tooltip title="Critical event">
                      <CriticalIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  {toggling === event.id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Switch
                      size="small"
                      checked={event.is_active}
                      onChange={(e) => onToggleActive(event, e.target.checked)}
                      color="success"
                    />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ width: 48 }}>
                  <Tooltip title="Edit event">
                    <IconButton size="small" onClick={() => onEdit(event)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const ProgrammeEditor: React.FC = () => {
  const qc = useQueryClient();

  const [programmeId, setProgrammeId] = useState<number | ''>('');
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [copying, setCopying] = useState(false);
  const [copyProgress, setCopyProgress] = useState(0);
  const [copyDone, setCopyDone] = useState(false);
  const [regenSuccess, setRegenSuccess] = useState(false);

  const { data: programmes, isLoading: programmesLoading } = usePreventiveProgrammes();
  const { data: programme, isLoading: programmeLoading } = usePreventiveProgramme(
    programmeId || undefined
  );
  const { data: products } = usePHProducts({ per_page: 100, is_active: true });

  const updateEvent = useUpdateProgrammeEvent();
  const storeProgramme = useStoreProgramme();
  const storeProgrammeEvent = useStoreProgrammeEvent();
  const regenerate = useRegenerateProgramme();

  const programmeList = programmes ?? [];
  const allEvents: any[] = useMemo(() => programme?.events ?? [], [programme]);

  const eventsByTrack = useMemo(() => {
    return TRACKS.reduce<Record<string, any[]>>((acc, track) => {
      acc[track] = allEvents
        .filter((e) => e.track === track)
        .sort((a, b) => (a.sequence_order ?? 999) - (b.sequence_order ?? 999));
      return acc;
    }, {});
  }, [allEvents]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleToggleActive = async (event: any, active: boolean) => {
    setTogglingId(event.id);
    try {
      await updateEvent.mutateAsync({ id: event.id, data: { is_active: active } });
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveEvent = async (data: any) => {
    if (!editingEvent) return;
    await updateEvent.mutateAsync({ id: editingEvent.id, data });
    setEditingEvent(null);
  };

  const handleSaveAsNewVersion = async (name: string, version: string) => {
    setCopying(true);
    setCopyProgress(0);
    setCopyDone(false);
    try {
      const newProg = await storeProgramme.mutateAsync({
        name,
        version,
        description: programme?.description ?? null,
        is_active: false,
      });

      const events = allEvents;
      for (let i = 0; i < events.length; i++) {
        const e = events[i];
        await storeProgrammeEvent.mutateAsync({
          programmeId: newProg.id,
          data: {
            event_code: e.event_code,
            name: e.name,
            track: e.track,
            trigger_type: e.trigger_type,
            trigger_value: e.trigger_value,
            description: e.description,
            recurrence: e.recurrence,
            product_id: e.product?.id ?? null,
            dose_instructions: e.dose_instructions,
            route: e.route,
            egg_withdrawal_days: e.egg_withdrawal_days,
            duration_days: e.duration_days,
            sequence_order: e.sequence_order,
            is_critical: e.is_critical ?? false,
            is_active: e.is_active ?? true,
          },
        });
        setCopyProgress(Math.round(((i + 1) / events.length) * 100));
      }

      setCopyDone(true);
      qc.invalidateQueries({ queryKey: ['ph', 'programmes'] });
      setTimeout(() => setProgrammeId(newProg.id), 800);
    } catch {
      // keep dialog open so user can see the failure state
    } finally {
      setCopying(false);
    }
  };

  const handleRegenerate = async () => {
    if (!programmeId) return;
    await regenerate.mutateAsync(programmeId as number);
    setRegenSuccess(true);
    setTimeout(() => setRegenSuccess(false), 3000);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const isLoading = programmesLoading || (!!programmeId && programmeLoading);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Page header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <AutoFixIcon sx={{ color: '#ff9800', fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Programme Editor
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Edit events, doses, and dependencies across all tracks
          </Typography>
        </Box>
      </Box>

      {/* Programme selector + action buttons */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Select programme</InputLabel>
          <Select
            value={programmeId}
            label="Select programme"
            onChange={(e) => setProgrammeId(e.target.value as number | '')}
          >
            <MenuItem value="">— choose —</MenuItem>
            {programmeList.map((p: any) => (
              <MenuItem key={p.id} value={p.id}>
                <Box display="flex" alignItems="center" gap={1}>
                  {p.name}
                  {p.version && (
                    <Chip label={`v${p.version}`} size="small" sx={{ fontSize: '0.68rem' }} />
                  )}
                  {!p.is_active && (
                    <Chip
                      label="inactive"
                      size="small"
                      color="default"
                      sx={{ fontSize: '0.68rem' }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {programme && (
          <Box display="flex" alignItems="center" gap={1} ml="auto" flexWrap="wrap">
            <Chip
              label={programme.is_active ? 'Active' : 'Inactive'}
              color={programme.is_active ? 'success' : 'default'}
              size="small"
            />
            <Tooltip title="Regenerate schedules for all batches using this programme">
              <Button
                size="small"
                variant="outlined"
                startIcon={regenerate.isPending ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={handleRegenerate}
                disabled={regenerate.isPending}
                color={regenSuccess ? 'success' : 'primary'}
              >
                {regenSuccess ? 'Queued!' : 'Regenerate Schedules'}
              </Button>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => {
                setCopyDone(false);
                setCopyProgress(0);
                setVersionDialogOpen(true);
              }}
            >
              Save as New Version
            </Button>
          </Box>
        )}
      </Paper>

      {/* Loading state */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && !programmeId && (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={8}>
          <VaccinesIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h6" color="text.secondary">
            Select a programme to edit
          </Typography>
        </Box>
      )}

      {/* Track sections */}
      {!isLoading && programme && (
        <Box>
          {TRACKS.map((track) => (
            <TrackSection
              key={track}
              track={track}
              events={eventsByTrack[track] ?? []}
              onEdit={setEditingEvent}
              onToggleActive={handleToggleActive}
              toggling={togglingId}
            />
          ))}
        </Box>
      )}

      {/* Event edit dialog */}
      {editingEvent && (
        <EventEditDialog
          event={editingEvent}
          allEvents={allEvents}
          products={products}
          onClose={() => setEditingEvent(null)}
          onSave={handleSaveEvent}
          saving={updateEvent.isPending}
        />
      )}

      {/* Save as new version dialog */}
      {versionDialogOpen && programme && (
        <SaveVersionDialog
          programme={programme}
          onClose={() => {
            if (!copying) setVersionDialogOpen(false);
          }}
          onConfirm={handleSaveAsNewVersion}
          copying={copying}
          copyProgress={copyProgress}
          copyDone={copyDone}
        />
      )}
    </Box>
  );
};

export default ProgrammeEditor;
